import { useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'mitzvafit_session_id';
// Cart stored in localStorage (non-PII, just product selections and quantities)
const CART_KEY = 'mitzvafitCart';

const getSessionId = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = 'sess_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

export const useCartPersistence = () => {
  const [cart, setCart] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const syncTimeout = useRef(null);
  const sessionId = useRef(getSessionId());

  // Load cart from backend on mount (one-shot).
  // API_URL + CART_KEY are module constants, sessionId is a ref (stable).
  useEffect(() => {
    const loadCart = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cart/${sessionId.current}`);
        if (res.ok) {
          const data = await res.json();
          if (data.items && data.items.length > 0) {
            localStorage.setItem(CART_KEY, JSON.stringify(data.items));
            setCart(data.items);
            window.dispatchEvent(new Event('cartUpdated'));
          } else {
            // Fall back to localStorage
            const local = JSON.parse(localStorage.getItem(CART_KEY) || '[]');
            setCart(local);
          }
        }
      } catch {
        const local = JSON.parse(localStorage.getItem(CART_KEY) || '[]');
        setCart(local);
      } finally {
        setLoaded(true);
      }
    };
    loadCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync to backend (debounced).
  // API_URL is module const, sessionId is ref — both stable across renders.
  const syncToBackend = useCallback((items) => {
    if (syncTimeout.current) clearTimeout(syncTimeout.current);
    syncTimeout.current = setTimeout(async () => {
      try {
        await fetch(`${API_URL}/api/cart`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId.current, items })
        });
      } catch (e) {
        console.error('Cart sync failed:', e);
      }
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for cart changes from other components
  useEffect(() => {
    const handleCartUpdate = () => {
      const items = JSON.parse(localStorage.getItem(CART_KEY) || '[]');
      setCart(items);
      syncToBackend(items);
    };

    const handleStorageChange = (e) => {
      if (e.key === CART_KEY) handleCartUpdate();
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('storage', handleStorageChange);
      if (syncTimeout.current) clearTimeout(syncTimeout.current);
    };
  }, [syncToBackend]);

  return { cart, loaded, sessionId: sessionId.current };
};
